import { useEffect } from "react";
import { create } from "zustand";

import { useTranslate } from "../../services";

/** ページ名ストア */
interface PageNameStore {
  /**
   * ページ名を追加する。
   * @param pageName ページ名
   */
  addPageName(pageName: string): void;
  /** ページ名群 */
  pageNames: Array<string>;
  /**
   * ページ名を削除する。
   * @param pageName ページ名
   */
  removePageName(pageName: string): void;
}

/** ページ名ストアを使用する。 */
const usePageNameStore = create<PageNameStore>(function (set) {
  return {
    addPageName(pageName: string) {
      set(function ({ pageNames }) {
        return { pageNames: [...pageNames, pageName] };
      });
    },
    pageNames: [],
    removePageName(pageName: string) {
      set(function ({ pageNames }) {
        /** 既に1つ削除した場合、`true` */
        let removed = false;
        /** 新しいページ名群 */
        const pns: typeof pageNames = [];
        // 最も後ろに出現する一致したページ名を削除する。
        for (const pn of pageNames.reverse()) {
          if (!removed && pn === pageName) {
            removed = true;
          } else {
            pns.push(pn);
          }
        }
        return { pageNames: pns };
      });
    },
  };
});

/** ページ名のプロパティ */
export type PageNameProperties = { children: string };

/**
 * ページ名
 * `<title />`を優先度付きで追加する。最後にレンダリングされたページ名が最優先される。
 */
export function PageName({ children }: PageNameProperties) {
  const { addPageName, removePageName, pageNames } = usePageNameStore();
  const translate = useTranslate();

  useEffect(
    function () {
      addPageName(children);
      return function () {
        removePageName(children);
      };
    },
    [addPageName, children, removePageName],
  );

  /** アプリケーション名 */
  const applicationName = translate("%ApplicationName.0%");
  /** ページ名 */
  const pageName = pageNames.at(0);

  return (
    <title>{pageName !== undefined ? pageName + " | " + applicationName : applicationName}</title>
  );
}
